import { useState, useEffect, useCallback, useRef } from 'react';
import { SERVER_URL } from '../utils/constants';
import { UNAUTHORIZED_EVENT, installUnauthorizedInterceptor, markAccessGranted } from '../utils/auth';

installUnauthorizedInterceptor();

// While the backend is down (Caddy answers 502/503/504) the session check is retried
const SESSION_RETRY_MS = 3000;
const SERVER_UNAVAILABLE_MSG = 'เซิร์ฟเวอร์ไม่พร้อมใช้งาน กรุณาลองใหม่อีกครั้ง';

// Access is a server-side session: the code buys an HttpOnly cookie, and GET /api/session
// tells whether this browser still holds a valid one.
export function useAccessControl() {
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [accessError, setAccessError] = useState('');
  const [clientIp, setClientIp] = useState(null);
  const [accessChecking, setAccessChecking] = useState(true);
  const grantedRef = useRef(false);
  grantedRef.current = accessGranted;

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const checkSession = async () => {
      // A code entered meanwhile already granted access
      if (grantedRef.current) return;
      let retry = false;
      try {
        const resp = await fetch(`${SERVER_URL}/api/session`, { cache: 'no-store' });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (data.ip) setClientIp(data.ip);
        if (resp.status >= 500) {
          retry = true;
        } else if (resp.ok && data.authenticated === true) {
          markAccessGranted();
          setAccessGranted(true);
        }
      } catch (err) {
        console.warn('Session check failed', err);
        retry = true;
      } finally {
        if (!cancelled) {
          setAccessChecking(false);
          if (retry) retryTimer = setTimeout(checkSession, SESSION_RETRY_MS);
        }
      }
    };

    checkSession();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, []);

  useEffect(() => {
    const onUnauthorized = () => {
      if (!grantedRef.current) return;
      setAccessGranted(false);
      setAccessError('เซสชันหมดอายุ กรุณาใส่รหัสอีกครั้ง');
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const handleAccessSubmit = useCallback(async () => {
    setAccessError('');
    try {
      const resp = await fetch(`${SERVER_URL}/api/verify-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: accessCode.trim() }),
      });
      const data = await resp.json().catch(() => ({}));

      if (resp.ok && data.success) {
        markAccessGranted();
        setAccessGranted(true);
        return true;
      }

      if (resp.status === 429) setAccessError('ลองผิดหลายครั้งเกินไป กรุณารอ 1 นาที');
      else if (resp.status === 403) setAccessError('รหัสไม่ถูกต้อง');
      else if (resp.status >= 500) setAccessError(SERVER_UNAVAILABLE_MSG);
      else setAccessError(data.error || 'Invalid code');
      return false;
    } catch (err) {
      setAccessError(SERVER_UNAVAILABLE_MSG);
      return false;
    }
  }, [accessCode]);

  return {
    accessGranted,
    accessCode,
    setAccessCode,
    accessError,
    clientIp,
    accessChecking,
    handleAccessSubmit,
  };
}
